import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlashFonicProvider, useFlashFonic } from '../contexts/FlashFonicContext.jsx';
import ContentRenderer from './ContentRenderer.jsx';
import HowToPlayModal from './HowToPlayModal.jsx';
import EnterNameModal from './EnterNameModal.jsx';
import { trackEvent, authApi, authApiIdempotent } from '../firebase/config.js';
import { Events } from '../firebase/events.js';
import { ENV } from '../config/env.js';
import { loadTone } from '../utils/lazyTone.js';

const API_BASE_URL = ENV.apiBaseUrl;

const getMultiplier = (streak) => streak >= 6 ? 2 : streak >= 3 ? 1.5 : 1;

const getMedal = (finalScore, deckLength) => {
    const max = deckLength * 100;
    if (!max) return { name: 'Mnemonic Casualty', icon: '🩹', grad: 'from-slate-600 to-slate-500', pct: 0 };
    const pct = (finalScore / max) * 100;
    if (pct === 100) return { name: 'Verbatim Master', icon: '🏆', grad: 'from-yellow-400 to-amber-500', pct };
    if (pct >= 90)   return { name: 'Synapse Slayer',  icon: '🧠', grad: 'from-rose-500 to-orange-500', pct };
    if (pct >= 80)   return { name: 'Recall Assassin', icon: '🗡️',  grad: 'from-blue-500 to-cyan-400', pct };
    if (pct >= 70)   return { name: 'Mind Sniper',     icon: '🎯', grad: 'from-emerald-500 to-teal-400', pct };
    return { name: 'Mnemonic Casualty', icon: '🩹', grad: 'from-slate-600 to-slate-500', pct };
};

// ── Voice selector ─────────────────────────────────────────────────────────────
const VoiceSelector = ({ voices, selectedVoice, onChange }) => (
    <select
        value={selectedVoice}
        onChange={e => onChange(e.target.value)}
        className="w-full rounded-xl bg-slate-800/70 border border-white/10 px-3 py-2 text-sm text-slate-200 focus:outline-none"
    >
        {voices.map(v => <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>)}
    </select>
);

// ── Streak badge ───────────────────────────────────────────────────────────────
const StreakBadge = ({ streak }) => {
    if (streak < 3) return null;
    const mult = getMultiplier(streak);
    return (
        <div className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${streak >= 6 ? 'bg-amber-500/20 text-amber-300' : 'bg-orange-500/20 text-orange-300'}`}>
            🔥 {streak} streak · {mult}×
        </div>
    );
};

// ── Score ring countdown ───────────────────────────────────────────────────────
const CountdownRing = ({ remainingMs, totalMs = 7000 }) => {
    const radius = 36;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(1, Math.max(0, (totalMs - remainingMs) / totalMs));
    const dash = circumference * progress;
    const isLow = remainingMs < 2500;
    return (
        <div className="relative h-28 w-28 mx-auto">
            <svg width="112" height="112" viewBox="0 0 112 112" className="mx-auto">
                <circle cx="56" cy="56" r={radius} stroke="#1e293b" strokeWidth="8" fill="none" />
                <circle cx="56" cy="56" r={radius} stroke={isLow ? '#f43f5e' : '#a21caf'} strokeWidth="8" fill="none"
                    strokeDasharray={`${dash} ${circumference - dash}`} strokeLinecap="round"
                    transform="rotate(-90 56 56)" style={{ transition: 'stroke-dasharray 0.1s linear' }} />
            </svg>
            <div className={`absolute inset-0 grid place-items-center text-2xl font-bold tabular-nums ${isLow ? 'text-rose-300' : 'text-white'}`}>
                {Math.ceil(remainingMs / 1000)}
            </div>
        </div>
    );
};

// ── Main game component ────────────────────────────────────────────────────────
const GameViewerInner = ({ folder, onClose, onBackToStudy, onExitGame, cameFromStudy, mostRecentScore }) => {
    const [deck, setDeck] = useState(() => [...folder.cards].sort(() => Math.random() - 0.5));
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [gameState, setGameState] = useState('landing');
    const [userAnswer, setUserAnswer] = useState('');
    const [lastResult, setLastResult] = useState(null);
    const [showHowToPlay, setShowHowToPlay] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [playerName, setPlayerName] = useState('');
    const [showLeaderboard, setShowLeaderboard] = useState(false);
    const [playMode, setPlayMode] = useState('continuous');
    const [inputMode, setInputMode] = useState('voice');
    const [cardFace, setCardFace] = useState('question');
    const [streak, setStreak] = useState(0);
    const [history, setHistory] = useState([]);
    const [hintUsed, setHintUsed] = useState(false);
    const [hintText, setHintText] = useState('');
    const [typedAnswer, setTypedAnswer] = useState('');
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState('');
    const [remainingMs, setRemainingMs] = useState(0);
    const [showHistory, setShowHistory] = useState(false);

    const typedInputRef = useRef(null);
    const ff = useFlashFonic();
    const currentCard = deck[currentIndex];
    const multiplier = getMultiplier(streak);

    // ── Sounds ─────────────────────────────────────────────────────────────────
    // Tone.js is a full synthesis library — a disproportionate chunk of the eagerly-loaded
    // main bundle for four short game-over/answer chimes. Loaded on first actual use instead
    // (and cached), so a landing-page or study-mode visit never pays for it.
    const sounds = useMemo(() => {
        const play = (fn) => { loadTone().then(fn).catch(() => {}); };
        const mk = (Tone, type) => new Tone.Synth({ oscillator: { type }, envelope: { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.2 } }).toDestination();
        return {
            perfect: () => play((Tone) => { const s = mk(Tone, 'sine'); s.triggerAttackRelease('C5', '8n', Tone.now()); s.triggerAttackRelease('G5', '8n', Tone.now() + 0.1); }),
            good:    () => play((Tone) => mk(Tone, 'triangle').triggerAttackRelease('E5', '8n')),
            ok:      () => play((Tone) => mk(Tone, 'square').triggerAttackRelease('C4', '8n')),
            wrong:   () => play((Tone) => new Tone.NoiseSynth().toDestination().triggerAttackRelease('4n')),
        };
    }, []);

    // ── TTS ────────────────────────────────────────────────────────────────────
    const speak = useCallback((text, onEnd) => {
        try { ff?.stopListening && ff.stopListening(); } catch {}
        window.speechSynthesis.cancel();
        const utt = new SpeechSynthesisUtterance(text);
        const voice = voices.find(v => v.name === selectedVoice);
        if (voice) utt.voice = voice;
        utt.rate = 1;
        utt.onend = () => { if (onEnd) onEnd(); };
        window.speechSynthesis.speak(utt);
    }, [voices, selectedVoice, ff]);

    // ── Voice loader ───────────────────────────────────────────────────────────
    useEffect(() => {
        const load = () => {
            const evs = window.speechSynthesis.getVoices().filter(v => v.lang.startsWith('en'));
            setVoices(evs);
            if (evs.length > 0 && !selectedVoice) setSelectedVoice(evs[0].name);
        };
        window.speechSynthesis.onvoiceschanged = load;
        load();
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, [selectedVoice]);

    // ── Refs ───────────────────────────────────────────────────────────────────
    const recordingStartedRef = useRef(false);
    const countdownIntervalRef = useRef(null);
    const captureTimerRef = useRef(null);
    const hasAdvancedRef = useRef(false);
    const speakingTimeoutRef = useRef(null);
    // Keep current submitAnswer stable ref for async callbacks
    const submitAnswerRef = useRef(null);

    // ── nextRound ──────────────────────────────────────────────────────────────
    const nextRound = useCallback((pointsFromThisRound = 0) => {
        setUserAnswer('');
        setLastResult(null);
        setHintUsed(false);
        setHintText('');
        setTypedAnswer('');
        if (currentIndex < deck.length - 1) {
            setCurrentIndex(i => i + 1);
            setGameState('starting');
        } else {
            const finalScore = score + pointsFromThisRound;
            const medal = getMedal(finalScore, deck.length);
            onClose(folder.id, finalScore, playerName, medal.name);
            setGameState('game_over');
            trackEvent(Events.game_end, { folderId: folder.id, folderName: folder.name, finalScore, deckSize: deck.length, playerName: playerName || 'ANONYMOUS', playMode });
        }
    }, [currentIndex, deck.length, onClose, folder.id, folder.name, score, playerName, playMode]);

    // ── submitAnswer ──────────────────────────────────────────────────────────
    const submitAnswer = useCallback(async (rawText) => {
        const text = (rawText || '').trim();
        setUserAnswer(text);
        const hinted = hintUsed;
        const currentMult = getMultiplier(streak);

        const advanceAfterFeedback = (pts) => {
            setCardFace('answer');
            if (playMode === 'continuous') {
                hasAdvancedRef.current = false;
                clearTimeout(speakingTimeoutRef.current);
                speakingTimeoutRef.current = setTimeout(() => {
                    if (!hasAdvancedRef.current) { hasAdvancedRef.current = true; nextRound(pts); }
                }, 30000);
            }
        };

        if (!text) {
            setLastResult({ rawScore: 0, finalScore: 0, hinted });
            setStreak(0);
            setHistory(prev => [...prev, { card: currentCard, userAnswer: '', rawScore: 0, finalScore: 0, hinted }]);
            sounds.wrong();
            advanceAfterFeedback(0);
            speak(`I didn't hear an answer. The correct answer was: ${currentCard.answer}`, () => {
                if (playMode === 'continuous' && !hasAdvancedRef.current) {
                    hasAdvancedRef.current = true;
                    clearTimeout(speakingTimeoutRef.current);
                    nextRound(0);
                }
            });
            setGameState('round_result');
            return;
        }

        try {
            const data = await authApiIdempotent(`${API_BASE_URL}/score-answer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userAnswer: text, correctAnswer: currentCard.answer }),
            });

            let rawScore = Number(data.score ?? 0);
            if (hinted && rawScore > 60) rawScore = 60;
            const finalScore = Math.round(rawScore * currentMult);
            const isCorrect = rawScore >= 70;
            const isPartial = rawScore >= 30 && rawScore < 70;

            setLastResult({ rawScore, finalScore, hinted, mult: currentMult > 1 ? currentMult : null });
            setHistory(prev => [...prev, { card: currentCard, userAnswer: text, rawScore, finalScore, hinted }]);

            if (isCorrect) {
                setStreak(s => s + 1);
                setScore(s => s + finalScore);
                if (rawScore === 100) sounds.perfect(); else sounds.good();
            } else if (isPartial) {
                setStreak(0);
                setScore(s => s + finalScore);
                sounds.ok();
            } else {
                setStreak(0);
                sounds.wrong();
            }

            let speech = '';
            if (isCorrect) speech = `Correct! ${finalScore} points${currentMult > 1 ? `, with a ${currentMult}× streak bonus` : ''}.`;
            else if (isPartial) speech = `Partially correct for ${finalScore} points. The answer was: ${currentCard.answer}`;
            else speech = `Incorrect. The correct answer was: ${currentCard.answer}`;

            advanceAfterFeedback(finalScore);
            speak(speech, () => {
                if (playMode === 'continuous' && !hasAdvancedRef.current) {
                    hasAdvancedRef.current = true;
                    clearTimeout(speakingTimeoutRef.current);
                    nextRound(finalScore);
                }
            });
            setGameState('round_result');
        } catch (err) {
            console.error('Score error:', err);
            setLastResult({ rawScore: 0, finalScore: 0, hinted });
            setStreak(0);
            setHistory(prev => [...prev, { card: currentCard, userAnswer: text, rawScore: 0, finalScore: 0, hinted }]);
            sounds.wrong();
            advanceAfterFeedback(0);
            speak(`Error scoring. The correct answer was: ${currentCard.answer}`, () => {
                if (playMode === 'continuous' && !hasAdvancedRef.current) {
                    hasAdvancedRef.current = true;
                    clearTimeout(speakingTimeoutRef.current);
                    nextRound(0);
                }
            });
            setGameState('round_result');
        }
    }, [currentCard, sounds, playMode, nextRound, speak, hintUsed, streak]);

    // Keep ref current for capture callbacks
    useEffect(() => { submitAnswerRef.current = submitAnswer; }, [submitAnswer]);

    // ── Hint ──────────────────────────────────────────────────────────────────
    const handleHint = () => {
        if (hintUsed || !currentCard) return;
        const firstWord = String(currentCard.answer).split(/\s+/)[0];
        setHintText(`First word: "${firstWord}"`);
        setHintUsed(true);
    };

    // ── Voice recording ───────────────────────────────────────────────────────
    const startRecording = useCallback(async () => {
        if (recordingStartedRef.current) return;
        recordingStartedRef.current = true;
        try {
            setGameState('listening');
            if (!ff?.isListening) {
                ff?.setCaptureLastSeconds && ff.setCaptureLastSeconds(7);
                await ff?.startListening();
            }
            const totalWindowMs = 10000;
            clearInterval(countdownIntervalRef.current);
            setRemainingMs(totalWindowMs);
            countdownIntervalRef.current = setInterval(() => {
                setRemainingMs(prev => {
                    const next = Math.max(0, prev - 100);
                    if (next === 0) clearInterval(countdownIntervalRef.current);
                    return next;
                });
            }, 100);
            captureTimerRef.current = setTimeout(async () => {
                try {
                    const clip = await ff?.captureClip?.();
                    clearInterval(countdownIntervalRef.current);
                    setRemainingMs(0);
                    if (!clip?.blob) { submitAnswerRef.current?.(''); return; }
                    const reader = new FileReader();
                    reader.readAsDataURL(clip.blob);
                    reader.onloadend = async () => {
                        const base64Audio = String(reader.result || '').split(',')[1];
                        try {
                            const payload = { audio_data: base64Audio, mime_type: clip.mimeType || 'audio/webm', duration: clip.durationSec || 7, engine: 'openai' };
                            if (currentCard?.lang && String(currentCard.lang).toLowerCase() !== 'auto') payload.lang = currentCard.lang;
                            const data = await authApi(`${API_BASE_URL}/transcribe-answer`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
                            submitAnswerRef.current?.(data.transcript);
                        } catch { submitAnswerRef.current?.('[Error transcribing audio]'); }
                    };
                } catch { submitAnswerRef.current?.(''); }
            }, totalWindowMs);
        } catch { submitAnswerRef.current?.(''); }
    }, [ff, currentCard]);

    // ── Ask question ──────────────────────────────────────────────────────────
    const askQuestion = useCallback(() => {
        if (!currentCard) return;
        recordingStartedRef.current = false;
        clearTimeout(captureTimerRef.current);
        clearInterval(countdownIntervalRef.current);
        setGameState('asking');
        setCardFace('question');
        if (inputMode === 'voice') {
            speak(`Question: ${currentCard.question}`, () => {
                setGameState('listening');
                startRecording();
            });
        } else {
            // type mode: just show question, focus input
            setGameState('listening');
            setTimeout(() => typedInputRef.current?.focus(), 50);
        }
    }, [currentCard, speak, startRecording, inputMode]);

    // 'starting' → ask after brief pause
    useEffect(() => {
        if (gameState === 'starting') {
            const t = setTimeout(askQuestion, inputMode === 'type' ? 500 : 3000);
            return () => clearTimeout(t);
        }
    }, [gameState, askQuestion, inputMode]);

    // ── Stop all audio ────────────────────────────────────────────────────────
    const stopAllAudio = () => {
        window.speechSynthesis.cancel();
        try { ff?.stopListening && ff.stopListening(); } catch {}
        clearInterval(countdownIntervalRef.current);
        clearTimeout(captureTimerRef.current);
        clearTimeout(speakingTimeoutRef.current);
        clearTimeout(recordingStartedRef.current);
    };

    useEffect(() => () => { try { stopAllAudio(); } catch {} }, []);

    const handleExit = () => { stopAllAudio(); onExitGame(); };
    const handleBackButton = () => { stopAllAudio(); cameFromStudy ? onBackToStudy(folder) : onExitGame(); };
    const handlePause = () => { stopAllAudio(); setIsPaused(true); };
    const handleResume = () => { setIsPaused(false); askQuestion(); };

    const startGame = () => {
        const primingUtt = new SpeechSynthesisUtterance(' ');
        primingUtt.volume = 0;
        window.speechSynthesis.speak(primingUtt);
        setGameState('starting');
    };

    const playAgain = () => {
        setDeck([...folder.cards].sort(() => Math.random() - 0.5));
        setCurrentIndex(0); setScore(0); setStreak(0);
        setUserAnswer(''); setLastResult(null); setHistory([]);
        setHintUsed(false); setHintText(''); setTypedAnswer('');
        setGameState(playerName ? 'ready' : 'name_entry');
    };

    // ── Type mode submit ──────────────────────────────────────────────────────
    const handleTypeSubmit = () => {
        if (!typedAnswer.trim() || gameState !== 'listening') return;
        stopAllAudio();
        submitAnswer(typedAnswer);
    };

    // ── In-game header ────────────────────────────────────────────────────────
    const renderHeader = () => (
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-white/8 bg-slate-950/90 backdrop-blur-sm">
            <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Card {currentIndex + 1}/{deck.length}</span>
                <StreakBadge streak={streak} />
            </div>
            <div className="flex items-center gap-4">
                <span className="tabular-nums text-sm font-bold text-white">{score} pts</span>
                <button onClick={handlePause} className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition">
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                </button>
                <button onClick={handleExit} className="rounded-full p-1.5 text-slate-400 hover:text-rose-400 hover:bg-white/10 transition">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>
        </div>
    );

    // ── Question card ─────────────────────────────────────────────────────────
    const renderCard = () => (
        <div className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-slate-900/70 p-5 shadow-lg">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">{cardFace === 'question' ? 'Question' : 'Answer'}</p>
            <p className="text-white text-base leading-relaxed">
                <ContentRenderer content={cardFace === 'question' ? currentCard?.question : currentCard?.answer} />
            </p>
        </div>
    );

    // ── Hint button ───────────────────────────────────────────────────────────
    const renderHintArea = () => (
        <div className="flex flex-col items-center gap-2 mt-4">
            {!hintUsed ? (
                <button onClick={handleHint}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium text-slate-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/8 transition">
                    🔍 Hint <span className="text-slate-500">(–40 pts max)</span>
                </button>
            ) : (
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-2 text-sm text-amber-300">
                    {hintText}
                </div>
            )}
        </div>
    );

    // ── Game states ───────────────────────────────────────────────────────────
    const renderGameState = () => {
        if (isPaused) return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
                <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900/95 p-8 text-center shadow-2xl">
                    <div className="text-4xl mb-4">⏸</div>
                    <h2 className="text-xl font-bold text-white mb-6">Paused</h2>
                    <div className="flex gap-3 justify-center">
                        <button onClick={handleResume} className="rounded-xl px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90">Resume</button>
                        <button onClick={handleExit} className="rounded-xl px-5 py-2.5 text-sm font-medium text-slate-200 bg-slate-800 ring-1 ring-white/10">Exit</button>
                    </div>
                </div>
            </div>
        );

        switch (gameState) {
            case 'landing': return (
                <div className="flex items-center justify-center min-h-[80vh] px-4">
                    <div className="w-full max-w-sm space-y-5">
                        <div className="text-center">
                            <div className="text-5xl mb-3">🏆</div>
                            <h1 className="text-2xl font-black tracking-tight text-white">Verbatim Master</h1>
                            <p className="text-sm text-slate-400 mt-1">{folder.name} · {folder.cards?.length} cards</p>
                        </div>

                        {/* Play mode */}
                        <div className="rounded-xl bg-white/5 p-1 flex gap-1">
                            {[['continuous', 'Auto-advance'], ['manual', 'Manual']].map(([v, l]) => (
                                <button key={v} onClick={() => setPlayMode(v)}
                                    className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${playMode === v ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                                    {l}
                                </button>
                            ))}
                        </div>

                        {/* Input mode */}
                        <div className="rounded-xl bg-white/5 p-1 flex gap-1">
                            {[['voice', '🎙 Voice'], ['type', '⌨ Type']].map(([v, l]) => (
                                <button key={v} onClick={() => setInputMode(v)}
                                    className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${inputMode === v ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                                    {l}
                                </button>
                            ))}
                        </div>

                        {/* Voice selector (only in voice mode) */}
                        {inputMode === 'voice' && voices.length > 0 && (
                            <div>
                                <label className="block text-xs text-slate-400 mb-1">Voice</label>
                                <VoiceSelector voices={voices} selectedVoice={selectedVoice} onChange={setSelectedVoice} />
                            </div>
                        )}

                        <div className="grid gap-2 pt-2">
                            <button onClick={() => setGameState('name_entry')}
                                className="w-full rounded-xl py-3 text-base font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 transition shadow-lg shadow-amber-600/20">
                                Start Game
                            </button>
                            <div className="grid grid-cols-2 gap-2">
                                <button onClick={() => setShowHowToPlay(true)}
                                    className="rounded-xl py-2.5 text-sm text-slate-200 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition">
                                    How to Play
                                </button>
                                <button onClick={() => setShowLeaderboard(true)}
                                    className="rounded-xl py-2.5 text-sm text-slate-200 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 transition">
                                    Leaderboard
                                </button>
                            </div>
                            <button onClick={handleBackButton} className="text-sm text-slate-500 hover:text-slate-300 transition py-1">← Back</button>
                        </div>
                    </div>
                </div>
            );

            case 'name_entry':
                return <EnterNameModal onClose={() => setGameState('landing')} onConfirm={(name) => { setPlayerName(name); setGameState('ready'); }} />;

            case 'ready':
                return (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <button onClick={startGame}
                            className="rounded-2xl px-10 py-5 text-2xl font-black text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 shadow-xl shadow-amber-600/30 transition active:scale-95">
                            START
                        </button>
                    </div>
                );

            case 'starting':
                return (
                    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
                        <div className="h-12 w-12 rounded-full border-2 border-amber-400/40 border-t-amber-400 animate-spin" />
                        <p className="text-slate-300 text-lg">Get ready…</p>
                        {streak >= 3 && <StreakBadge streak={streak} />}
                    </div>
                );

            case 'asking':
                return (
                    <div className="px-4 py-8 space-y-4">
                        {renderCard()}
                        <p className="text-center text-sm text-slate-400">Listening to question…</p>
                    </div>
                );

            case 'listening':
                return (
                    <div className="px-4 py-6 space-y-5">
                        {renderCard()}

                        {inputMode === 'voice' ? (
                            <div className="space-y-3">
                                <CountdownRing remainingMs={remainingMs} />
                                <p className="text-center text-sm text-slate-300">Speak your answer now</p>
                                {ff?.isSpeechApiAvailable && (
                                    <div className="mx-auto max-w-xl rounded-xl border border-white/10 bg-slate-900/60 px-4 py-3">
                                        <p className="text-xs text-slate-500 mb-1">Hearing:</p>
                                        <p className="text-sm text-slate-200 min-h-[1.5rem]">
                                            {ff?.transcriptText}
                                            <span className="text-slate-500">{ff?.interimTranscript}</span>
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="mx-auto max-w-xl space-y-2">
                                <textarea
                                    ref={typedInputRef}
                                    value={typedAnswer}
                                    onChange={e => setTypedAnswer(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleTypeSubmit(); } }}
                                    rows={3}
                                    placeholder="Type your answer… (Enter to submit)"
                                    className="w-full rounded-xl bg-slate-800/60 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-none"
                                />
                                <button onClick={handleTypeSubmit} disabled={!typedAnswer.trim()}
                                    className="w-full rounded-xl py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 disabled:opacity-40 transition">
                                    Submit (Enter)
                                </button>
                            </div>
                        )}

                        {renderHintArea()}
                    </div>
                );

            case 'round_result': {
                if (!lastResult) return null;
                const { rawScore, finalScore, hinted, mult } = lastResult;
                const isCorrect = rawScore >= 70;
                const isPartial = rawScore >= 30 && rawScore < 70;
                const label = isCorrect ? 'Correct!' : isPartial ? 'Partial' : 'Incorrect';
                const colorClass = isCorrect ? 'text-emerald-400' : isPartial ? 'text-amber-400' : 'text-rose-400';
                const bgClass = isCorrect ? 'border-emerald-500/30 bg-emerald-500/8' : isPartial ? 'border-amber-500/30 bg-amber-500/8' : 'border-rose-500/30 bg-rose-500/8';

                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 backdrop-blur-sm px-4">
                        <div className={`w-full max-w-lg rounded-2xl border ${bgClass} shadow-2xl p-7 text-center space-y-4`}>
                            {/* Big score */}
                            <div className={`text-6xl font-black tabular-nums ${colorClass}`}>
                                {isCorrect || isPartial ? `+${finalScore}` : '0'}
                            </div>
                            <p className={`text-xl font-bold ${colorClass}`}>{label}</p>

                            {/* Streak / bonus badge */}
                            {mult && isCorrect && (
                                <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/20 px-3 py-1 text-xs font-semibold text-amber-300">
                                    🔥 {mult}× streak multiplier applied
                                </div>
                            )}
                            {hinted && (
                                <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-700/50 px-3 py-1 text-xs text-slate-400">
                                    🔍 Hint used · capped at 60 pts
                                </div>
                            )}

                            {/* What you said */}
                            {userAnswer && !isCorrect && (
                                <div className="text-left rounded-xl bg-white/4 border border-white/8 px-4 py-3 space-y-2">
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">You said</p>
                                    <p className="text-sm text-slate-300">{userAnswer}</p>
                                </div>
                            )}

                            {/* Correct answer (on wrong/partial) */}
                            {!isCorrect && (
                                <div className="text-left rounded-xl bg-white/4 border border-white/8 px-4 py-3 space-y-2">
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Correct answer</p>
                                    <p className="text-sm text-white"><ContentRenderer content={currentCard?.answer} /></p>
                                </div>
                            )}

                            {playMode === 'manual' ? (
                                <button onClick={() => nextRound(finalScore)}
                                    className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition ${isCorrect ? 'bg-emerald-600 hover:bg-emerald-500' : isPartial ? 'bg-amber-600 hover:bg-amber-500' : 'bg-rose-600 hover:bg-rose-500'}`}>
                                    {currentIndex + 1 >= deck.length ? 'See Results' : 'Next Card →'}
                                </button>
                            ) : (
                                <p className="text-xs text-slate-500">Next card coming up…</p>
                            )}
                        </div>
                    </div>
                );
            }

            case 'game_over': {
                const finalScore = score;
                const medal = getMedal(finalScore, deck.length);
                const maxScore = deck.length * 100;
                const pct = maxScore > 0 ? Math.round((finalScore / maxScore) * 100) : 0;

                // Build leaderboard
                const lb = [...(folder.leaderboard || [])];
                if (mostRecentScore && !lb.some(e => e.id === mostRecentScore.id)) lb.push(mostRecentScore);
                const sortedLb = lb.sort((a, b) => b.score - a.score);

                return (
                    <div className="px-4 py-8 space-y-6 max-w-2xl mx-auto">
                        {/* Medal */}
                        <div className="text-center space-y-2">
                            <div className={`inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br ${medal.grad} text-4xl shadow-xl`}>
                                {medal.icon}
                            </div>
                            <h2 className="text-2xl font-black text-white">{medal.name}</h2>
                            <p className="text-slate-400 text-sm">{folder.name}</p>
                        </div>

                        {/* Score */}
                        <div className="rounded-2xl border border-white/10 bg-slate-900/70 p-5">
                            <div className="flex items-end justify-between mb-3">
                                <div>
                                    <p className="text-3xl font-black text-white tabular-nums">{mostRecentScore?.score ?? finalScore}</p>
                                    <p className="text-xs text-slate-400">out of {maxScore} pts</p>
                                </div>
                                <span className={`text-2xl font-bold tabular-nums ${pct >= 80 ? 'text-emerald-400' : pct >= 60 ? 'text-amber-400' : 'text-rose-400'}`}>{pct}%</span>
                            </div>
                            <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
                                <div className={`h-full rounded-full bg-gradient-to-r ${medal.grad} transition-all duration-700`} style={{ width: `${pct}%` }} />
                            </div>
                        </div>

                        {/* Card-by-card history */}
                        <div>
                            <button onClick={() => setShowHistory(h => !h)}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 hover:bg-white/8 text-sm font-medium text-slate-200 transition">
                                <span>Review ({history.length} cards)</span>
                                <svg className={`h-4 w-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            {showHistory && (
                                <div className="mt-2 rounded-xl border border-white/8 overflow-hidden">
                                    {/* Header */}
                                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-2 bg-slate-900/60 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                        <span>Question</span>
                                        <span>Your Answer</span>
                                        <span className="text-right">Score</span>
                                    </div>
                                    {history.map((h, i) => {
                                        const color = h.rawScore >= 70 ? 'text-emerald-400' : h.rawScore >= 30 ? 'text-amber-400' : 'text-rose-400';
                                        return (
                                            <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-2.5 border-t border-white/5 text-xs">
                                                <span className="text-slate-300 truncate">{String(h.card?.question || '').slice(0, 60)}</span>
                                                <span className="text-slate-400 truncate">{h.userAnswer || <em className="text-slate-600">—</em>}</span>
                                                <span className={`font-semibold tabular-nums text-right ${color}`}>
                                                    {h.finalScore > h.rawScore ? `${h.finalScore}*` : h.rawScore}
                                                </span>
                                            </div>
                                        );
                                    })}
                                    {history.some(h => h.finalScore > h.rawScore) && (
                                        <p className="text-[10px] text-slate-600 px-3 py-2">* includes streak multiplier</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Leaderboard */}
                        {sortedLb.length > 0 && (
                            <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">High Scores</p>
                                <div className="rounded-xl border border-white/8 overflow-hidden">
                                    {sortedLb.slice(0, 8).map((entry, i) => (
                                        <div key={entry.id || i}
                                            className={`grid grid-cols-[auto_1fr_auto] gap-3 px-4 py-2.5 text-sm border-t border-white/5 first:border-0 ${entry.id === mostRecentScore?.id ? 'bg-amber-500/10' : ''}`}>
                                            <span className="text-slate-500 tabular-nums">#{i + 1}</span>
                                            <span className="text-slate-200 truncate">{(entry.name || 'ANONYMOUS').toUpperCase()}</span>
                                            <span className="text-slate-300 tabular-nums font-semibold">{entry.score}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 pb-4">
                            <button onClick={playAgain}
                                className="flex-1 rounded-xl py-3 text-sm font-semibold text-white bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 transition">
                                Play Again
                            </button>
                            <button onClick={() => onBackToStudy(folder)}
                                className="flex-1 rounded-xl py-3 text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 ring-1 ring-white/10 transition">
                                Back to Study
                            </button>
                        </div>
                    </div>
                );
            }

            default: return null;
        }
    };

    // ── Leaderboard overlay ────────────────────────────────────────────────────
    if (showLeaderboard) {
        const lb = [...(folder.leaderboard || [])].sort((a, b) => b.score - a.score);
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4">
                <div className="w-full max-w-md rounded-2xl border border-white/10 bg-slate-900/95 shadow-2xl p-6">
                    <h2 className="text-base font-semibold text-white mb-1">{folder.name}</h2>
                    <p className="text-xs text-slate-400 mb-4">Leaderboard</p>
                    {lb.length === 0 ? (
                        <p className="text-sm text-slate-500 py-4 text-center">No scores yet — be the first!</p>
                    ) : (
                        <div className="rounded-xl border border-white/8 overflow-hidden">
                            {lb.slice(0, 10).map((entry, i) => (
                                <div key={entry.id || i} className="grid grid-cols-[auto_1fr_auto_auto] gap-3 px-4 py-2.5 text-sm border-t border-white/5 first:border-0">
                                    <span className="text-slate-500">#{i + 1}</span>
                                    <span className="text-slate-200 truncate">{(entry.name || 'ANONYMOUS').toUpperCase()}</span>
                                    <span className="text-slate-500 text-xs">{entry.level || ''}</span>
                                    <span className="text-slate-200 tabular-nums font-semibold">{entry.score}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-5 flex justify-end">
                        <button onClick={() => setShowLeaderboard(false)} className="rounded-xl px-4 py-2 text-sm text-white bg-amber-600 hover:bg-amber-500 transition">
                            Back
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-40 bg-slate-950 overflow-y-auto">
            {showHowToPlay && <HowToPlayModal onClose={() => setShowHowToPlay(false)} />}
            {gameState !== 'landing' && gameState !== 'game_over' && gameState !== 'name_entry' && gameState !== 'ready' && renderHeader()}
            {renderGameState()}
        </div>
    );
};

// ── Wrapper (provides FlashFonicContext) ──────────────────────────────────────
const GameViewer = (props) => (
    <FlashFonicProvider
        isDevMode={false}
        usage={{ count: 0, limit: 999 }}
        duration={0}
        isSafari={/^((?!chrome|android).)*safari/i.test(navigator.userAgent)}
        setNotification={() => {}}
        generateFlashcardRequest={async () => {}}
    >
        <GameViewerInner {...props} />
    </FlashFonicProvider>
);

export default GameViewer;
